import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { consoleApi } from '@/lib/consoleApi';
import { collectReviewItems, firstSentence, sectionDoc, splitRegistryMeta, type ReviewItemKind } from '@/lib/reviewItems';
import { RegistryContent } from '@/components/RegistryContent';
import { SignOffPanel } from '@/components/SignOffPanel';
import { EditableConsole } from '@/components/EditableConsole';
import { AdminPage } from '@/admin';

const KIND_CLASS: Record<ReviewItemKind, string> = {
  PROVISIONAL: 'bg-warning-soft text-warning',
  MA_REQUIRED: 'bg-warning-soft text-warning',
  'MA note': 'bg-surface-sunken text-text-secondary',
};

function isObj(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function SectionCard({ title, badge, badgeClass, value }: { title: string; badge: string; badgeClass: string; value: unknown }) {
  return (
    <div className="mb-21 rounded-lg border border-border p-21">
      <div className="mb-8 flex items-center justify-between gap-8">
        <h2 className="text-lg font-semibold">{title}</h2>
        <span className={`whitespace-nowrap rounded-pill px-2 py-0.5 text-[11px] font-semibold ${badgeClass}`}>{badge}</span>
      </div>
      <RegistryContent value={value} depth={1} />
    </div>
  );
}

export function RegistryDetailPage() {
  const { registryId = '' } = useParams();
  const qc = useQueryClient();
  const reg = useQuery({ queryKey: ['registry', registryId], queryFn: () => consoleApi.getRegistry(registryId) });
  const hist = useQuery({ queryKey: ['signoff-history', registryId], queryFn: () => consoleApi.signOffHistory(registryId) });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['registry', registryId] });
    void qc.invalidateQueries({ queryKey: ['signoff-history', registryId] });
    void qc.invalidateQueries({ queryKey: ['review-queue'] });
  };

  const downloadReviewDoc = async () => {
    const html = await consoleApi.reviewDoc(registryId);
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${registryId}_review.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const d = reg.data;
  const { meta, body } = splitRegistryMeta(d?.content);
  const bodyObj = isObj(body) ? body : {};
  const about = String(bodyObj._about ?? '');
  const subsetKeys = d?.subset_keys ?? [];
  const bodyKeys = Object.keys(bodyObj).filter((k) => !k.startsWith('_'));
  const subsetSections = bodyKeys.filter((k) => subsetKeys.includes(k));
  // Detection calibration renders as a dedicated read-only banner (below), not a
  // raw context card — it is an ENGINEERING gate the MA sees but never signs
  // (owner correction 2026-07-22: "MA is not for writing a list of keywords").
  const CAL_KEYS = ['detection_calibration', 'detection_calibration_note'];
  const contextSections = bodyKeys.filter((k) => !subsetKeys.includes(k) && !CAL_KEYS.includes(k));
  const detCal = isObj(bodyObj.detection_calibration) ? bodyObj.detection_calibration : null;
  const detCalPassed = detCal?.passed === true;
  const batteryArtifact = typeof detCal?.battery_artifact === 'string' ? detCal.battery_artifact : '';
  const items = d ? collectReviewItems(d.content) : [];
  const isProvisional = String(meta.status ?? '').toLowerCase() === 'provisional';
  const hashShort = d?.version_hash?.slice(0, 16);
  const isEditable = Boolean(d?.editable);

  return (
    <AdminPage
      title={d?.label ?? registryId}
      description="Medical Advisory review & sign-off"
      backTo={{ to: '/', label: 'Review queue' }}
      actions={d ? (
        <button
          data-testid="download-review-doc"
          onClick={() => { void downloadReviewDoc(); }}
          className="whitespace-nowrap rounded border border-border-strong px-2.5 py-1 text-body-sm text-text-secondary hover:bg-surface-sunken"
        >Download review doc</button>
      ) : undefined}
    >
        {reg.isLoading ? <p className="mt-21 text-body-sm text-text-muted">Loading…</p> : null}
        {reg.isError ? <p className="mt-21 text-body-sm text-flagged">Could not load the registry.</p> : null}

        {d ? (
          <>
            {/* meta bar */}
            <div className="my-21 flex flex-wrap items-center gap-x-21 gap-y-8 border-y border-border py-13 text-body-sm text-text-secondary">
              <span>Version <strong className="text-text-primary">v{String(meta.version ?? d.version)}</strong></span>
              {meta.last_updated ? <span>Last updated <strong className="text-text-primary">{String(meta.last_updated)}</strong></span> : null}
              {meta.status ? <span>Status <span className={`rounded-pill px-2 py-0.5 text-[11px] font-semibold ${isProvisional ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success'}`}>{String(meta.status)}</span></span> : null}
              {hashShort ? <span>Content hash <span className="font-mono text-[12px]">{hashShort}…</span></span> : null}
            </div>

            {/* provisional callout */}
            {isProvisional || meta.notes ? (
              <div className="mb-21 rounded-lg border border-warning bg-warning-soft p-21 text-body-sm">
                {isProvisional ? <strong className="text-warning">Provisional — not yet clinically authoritative. </strong> : null}
                {meta.notes ? <span className="text-text-primary">{String(meta.notes)}</span> : null}
              </div>
            ) : null}

            {/* detection calibration — READ-ONLY engineering gate (data-driven:
                only companion-safety carries it). The MA sees whether detection
                has cleared the automated battery, but never signs it; framed so
                it's clear this is not their action and why the loop isn't live. */}
            {detCal ? (
              <div data-testid="detection-calibration" className="mb-21 rounded-lg border border-border p-21 text-body-sm">
                <div className="flex items-center justify-between gap-8">
                  <h2 className="text-base font-semibold">Detection calibration</h2>
                  <span className={`whitespace-nowrap rounded-pill px-2 py-0.5 text-[11px] font-semibold ${detCalPassed ? 'bg-success-soft text-success' : 'bg-warning-soft text-warning'}`}>
                    {detCalPassed ? 'PASSED' : 'NOT PASSED'}
                  </span>
                </div>
                <p className="mt-8 text-text-secondary">
                  An <strong>engineering</strong> gate, not your sign-off: detection — which messages trigger a safety
                  response — is proven by an automated test battery, not a keyword list you author. The safety loop goes
                  live only when this has passed <strong>and</strong> you have signed the response copy below. It is
                  flipped by the battery, never by hand.
                  {batteryArtifact ? <> Battery artifact <span className="font-mono text-[12px]">{batteryArtifact}</span>.</> : null}
                </p>
              </div>
            ) : null}

            {/* framing — driven by the registry's own metadata (label +
                provisional status), not hardcoded per-registry copy */}
            <div className="space-y-8 text-body">
              <h2 className="text-base font-semibold">What this is</h2>
              {about ? <p className="text-body-sm">{about}</p> : null}
              <p className="text-body-sm">
                <strong>{d.label ?? registryId}</strong> holds clinical decision values the engine reasons over.
                {isProvisional
                  ? <> It ships <strong>provisional</strong> — a safe starting set authored by the team, <strong>not clinically authoritative until a Medical Advisor reviews and signs it off.</strong></>
                  : <> Review the values below and confirm they are clinically appropriate, or record what should change.</>}
              </p>
            </div>

            {isEditable ? (
              /* MA edit-and-sign console (SPEC ma-editable-peri-registry) */
              <div className="mt-21">
                <EditableConsole detail={d} onChanged={refresh} />
              </div>
            ) : (
            <>
            <div className="space-y-8 text-body">
              <h2 className="mt-21 text-base font-semibold">What we need from you</h2>
              <p className="text-body-sm">Please review each section and confirm the clinical values are appropriate, or note what should change. Sign-off is keyed to {subsetSections.length ? <>the {subsetSections.map((k, i) => <span key={k}><em>{k}</em>{i < subsetSections.length - 1 ? ', ' : ''}</span>)}</> : 'the flagged sections'} below; context sections are shown for reference.</p>
              {subsetSections.length ? (
                <ul className="list-disc pl-21 space-y-1 text-body-sm">
                  {subsetSections.map((k) => {
                    const doc = firstSentence(sectionDoc(bodyObj[k]));
                    return <li key={k}><strong>{k}</strong>{doc ? <> — {doc}</> : null}</li>;
                  })}
                </ul>
              ) : null}

              <h2 className="mt-21 text-base font-semibold">How to sign off</h2>
              <p className="text-body-sm">When satisfied, use the sign-off panel below: record <strong>Approve</strong>, or <strong>Request changes</strong> with notes. It is recorded as an auditable sign-off against this exact version (v{String(meta.version ?? d.version)}{hashShort ? <>, hash <span className="font-mono text-[12px]">{hashShort}…</span></> : null}).</p>
            </div>

            {/* items flagged */}
            {items.length ? (
              <>
                <h2 className="mt-21 text-base font-semibold">Items flagged for your sign-off</h2>
                <p className="mb-8 text-body-sm text-text-secondary">Auto-collected — sections marked provisional, disposition cells needing a signed stance, and embedded advisory notes.</p>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-body-sm">
                    <thead>
                      <tr className="border-b border-border-strong text-left text-[12px] uppercase tracking-wide text-text-secondary">
                        <th className="p-2 font-semibold">Where</th><th className="p-2 font-semibold">Type</th><th className="p-2 font-semibold">What it needs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} className="border-b border-border align-top">
                          <td className="p-2 font-mono text-[11px] text-text-secondary">{it.where}</td>
                          <td className="p-2"><span className={`inline-block rounded-pill px-2 py-0.5 text-[11px] font-semibold ${KIND_CLASS[it.kind]}`}>{it.kind}</span></td>
                          <td className="p-2 text-text-secondary">{it.detail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}

            {/* sign-off subset sections */}
            {subsetSections.length ? (
              <>
                <h2 className="mt-21 mb-5 text-base font-semibold">Sign-off subset</h2>
                <p className="mb-13 text-body-sm text-text-secondary">The clinically-meaningful content the sign-off is recorded against.</p>
                {subsetSections.map((k) => (
                  <SectionCard key={k} title={k} badge="sign-off subset" badgeClass="bg-warning-soft text-warning" value={bodyObj[k]} />
                ))}
              </>
            ) : null}

            {/* context sections */}
            {contextSections.length ? (
              <>
                <h2 className="mt-13 mb-13 text-base font-semibold">Context <span className="font-normal text-text-secondary">(not part of the sign-off)</span></h2>
                {contextSections.map((k) => (
                  <SectionCard key={k} title={k} badge="context · owner-signed" badgeClass="bg-surface-sunken text-text-secondary" value={bodyObj[k]} />
                ))}
              </>
            ) : null}

            {/* awaiting authoring — the cells the MA must author before this can
                be signed. Approve is refused server-side while any remain, so we
                surface exactly what's outstanding rather than let a 422 surprise. */}
            {d.authored && !d.authored.authored ? (
              <div className="mt-21 rounded-lg border border-warning bg-warning-soft p-21">
                <h2 className="text-base font-semibold text-warning">Awaiting authoring before sign-off</h2>
                <p className="mt-5 mb-13 text-body-sm text-text-primary">
                  {d.authored.unauthored_paths.length} cell{d.authored.unauthored_paths.length === 1 ? '' : 's'} still
                  carry placeholder content. <strong>Approve is unavailable until every one is authored</strong> — you can
                  still request changes. Each path below is a value that must be filled before this content is
                  clinically authoritative.
                </p>
                <ul data-testid="unauthored-paths" className="space-y-1">
                  {d.authored.unauthored_paths.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-body-sm">
                      <span aria-hidden className="mt-0.5 text-warning">☐</span>
                      <span className="font-mono text-[12px] text-text-primary">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* the live sign-off action */}
            <div className="mt-21">
              <SignOffPanel
                registryId={registryId} status={d.status} version={d.version}
                versionHash={d.version_hash} onSigned={refresh}
                approveBlocked={d.authored && !d.authored.authored ? { paths: d.authored.unauthored_paths } : null}
              />
            </div>
            </>
            )}
          </>
        ) : null}

        {/* history */}
        <section className="mt-21">
          <h2 className="mb-8 text-base font-semibold">Sign-off history</h2>
          {hist.isLoading ? <p className="text-body-sm text-text-muted">Loading…</p> : null}
          {hist.data && hist.data.length === 0 ? <p className="rounded border border-border bg-surface p-13 text-body-sm text-text-muted">No sign-off records yet.</p> : null}
          {hist.data && hist.data.length > 0 ? (
            <ul data-testid="history-list" className="divide-y divide-border rounded border border-border bg-surface">
              {hist.data.map((rec, i) => (
                <li key={`${rec.timestamp}-${i}`} className="p-13">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{rec.decision === 'changes_requested' ? 'Changes requested' : 'Approved'}</span>
                    <span className="text-[11px] text-text-muted">{rec.timestamp}</span>
                  </div>
                  <div className="text-[11px] text-text-secondary">by {rec.reviewer} · v{rec.registry_version}</div>
                  {rec.notes ? <p className="mt-1 text-body-sm">{rec.notes}</p> : null}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
    </AdminPage>
  );
}
